import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
  CheckReferencesResponse,
  ColumnId,
  CreateWorkflowRequest,
  ExecuteWorkflowRequest,
  ExecuteWorkflowResponse,
  QueryNode,
  ResumeExecuteWorkflowRequest,
  ServerErrorResponse,
  UpdateWorkflowRequest,
  VerifyFormulaRequest,
  VerifyFormulaResponse,
} from '@blaze-shared/api-contracts-domain';
import { ApiUtilsService } from '@blaze-shared/connectors-data-access';
import { Pagination } from '@blaze-shared/datasets-domain';
import { XhrService } from '@blaze-shared/platform-data-access';
import { Workflow } from '@blaze-shared/workflow-domain';

@Injectable({ providedIn: 'root' })
export class WorkflowsApiService {
  private readonly apiUtilsService = inject(ApiUtilsService);
  private readonly xhrService = inject(XhrService);

  get(workflowId: string): Observable<Workflow> {
    return this.xhrService.get<Workflow>(`workflows/${workflowId}`);
  }

  getForProject(projectId: string): Observable<Workflow[]> {
    return this.xhrService.get<Workflow[]>('workflows', { projectId });
  }

  postExecute({
    allowPolling,
    channelId,
    debugMode = false,
    groups,
    inputs,
    pagination,
    query,
    workflowId,
  }: {
    allowPolling: boolean;
    channelId?: string;
    debugMode?: boolean;
    groups: (ColumnId | string)[] | null;
    inputs: any;
    pagination: Pagination | null;
    query: QueryNode | null;
    workflowId: string;
  }): Observable<ExecuteWorkflowResponse | ServerErrorResponse> {
    let body: ExecuteWorkflowRequest = {
      allowPolling,
      channelId,
      debug: debugMode,
      inputs,
      pagination,
      query,
      resumeComplexNodes: true,
      resumeShowOnForm: true,
      ...(groups?.length && { grouping: { groups } }),
    };
    const externalAccountRowId = this.apiUtilsService.getExternalAccountRowId();
    if (externalAccountRowId) {
      body = { ...body, externalAccountRowId };
    }
    return this.xhrService.post<ExecuteWorkflowResponse, ExecuteWorkflowRequest>(
      `workflows/${workflowId}/execute`,
      body,
    );
  }

  /**
   * @param nodeId - ID of the workflow node for which logging is done.
   * @param message - Message to log
   * @param context - Context object assiciated with the log.
   * @returns
   */
  postLog(workflowId: string, nodeId: string, message: string, context: any): Observable<unknown> {
    const body = { nodeId, message, context };
    return this.xhrService.post(`workflows/${workflowId}/execute/log`, body);
  }

  postResumeWorkflow(
    workflowId: string,
    resumeRequest: ResumeExecuteWorkflowRequest,
  ): Observable<ExecuteWorkflowResponse> {
    return this.xhrService.post<ExecuteWorkflowResponse, ResumeExecuteWorkflowRequest>(
      `workflows/${workflowId}/resume`,
      resumeRequest,
    );
  }

  postSchedule(workflowId: string): Observable<void> {
    return this.xhrService.post(`workflows/${workflowId}/schedule`, {});
  }

  postUnSchedule(workflowId: string): Observable<void> {
    return this.xhrService.post(`workflows/${workflowId}/unschedule`, {});
  }

  postVerifyFormula(
    workflowId: string,
    formula: string,
    nodeId: string,
  ): Observable<VerifyFormulaResponse> {
    return this.xhrService.post<VerifyFormulaResponse, VerifyFormulaRequest>(
      `workflows/${workflowId}/verifyformula`,
      { formula, nodeId },
    );
  }

  restoreDeletedWorkflow(workflowId: string): Observable<void> {
    return this.xhrService.post(`workflows/${workflowId}/undelete`, {});
  }

  /**
   * This function should not be used directly. Instead, please use createWorkflow found in workflow-crud.service.ts.
   * This is because we need to ensure that whenever we create a new workflow, input schemas are properly computed.
   */
  _create(request: CreateWorkflowRequest): Observable<Workflow> {
    return this.xhrService.post<Workflow, CreateWorkflowRequest>('workflows', request);
  }

  getReferences(workflowId: string): Observable<CheckReferencesResponse> {
    return this.xhrService.get(`workflows/${workflowId}/references`);
  }

  /**
   * This function should not be used directly. Instead, please use deleteWorkflow found in workflow-crud.service.ts.
   * We need to ensure that when we delete a workflow, connectors are properly updated when necessary.
   */
  _delete(workflowId: string): Observable<void> {
    return this.xhrService.delete(`workflows/${workflowId}`);
  }

  /**
   * This function should not be used directly. Instead, please use updateWorkflow found in workflow-crud.service.ts.
   * This is because we need to ensure that whenever we update a workflow, input schemas are properly refreshed.
   */
  _update(workflowId: string, request: UpdateWorkflowRequest): Observable<Workflow> {
    return this.xhrService.put<Workflow, UpdateWorkflowRequest>(`workflows/${workflowId}`, request);
  }
}
